import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCurrentCulture, setTabTitle } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { redirect } from "@state";
import { MobilePlatform } from "@state/stores/types";
import { useRootAppStore } from "@hooks";
import {
	SowSubmissionForm,
	SowMessageDisplay,
	SowSubmissionDocumentsOnly,
} from "./components";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import PlayerSowStore from "@state/stores/sow/PlayerSowStore";
import { useLocation } from "react-router-dom";
import LoaderSpinner from "@components-shared/LoaderSpinner";
import { PlayerSowStoreContext } from "@state/context/sow/PlayerSowStoreContext";
import { observer } from "mobx-react";
import { redirectToLogin } from "@utils/auth";

export const PlayerSowPage = observer(function PlayerSowPage() {
	const { pageStore } = useRootAppStore<MobilePlatform>();
	const { t } = useTranslation();

	const { search } = useLocation();

	useScrollRestoration({ type: ConfigType.Basic });

	const rootStore = useRootAppStore();

	const playerSowStore = useMemo(() => new PlayerSowStore(rootStore), []);

	useLayoutEffect(() => {
		pageStore.setSubPageHeader({
			title: t("PLAYER_SOW.TITLE"),
			backButton: () =>
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`),
		});
	}, []);

	useEffect(() => {
		setTabTitle(t("PLAYER_SOW.TITLE"));

		const queryParams = new URLSearchParams(search);

		const params: Record<string, string> = {};

		queryParams.forEach((value, key) => {
			params[key] = value;
		});

		if (!rootStore.userAuthStore.isLoggedIn) {
			redirectToLogin();
		}

		if (rootStore.userAuthStore.isLoggedIn) {
			onStoreInitialize(params.requestId, params.userId);
		}

		return () => {
			playerSowStore.onDispose();
		};
	}, []);

	const onStoreInitialize = async (requestId: string, userId: string) => {
		await playerSowStore.onInitialize(requestId, userId);
	};

	return (
		<div className="l--content">
			{playerSowStore.loader.isLoading && <LoaderSpinner isLoading />}
			<div className="container container--base">
				<PlayerSowStoreContext.Provider value={playerSowStore}>
					{playerSowStore.isSubmissionRequired && (
						<>
							{playerSowStore.isFormRequired && (
								<SowSubmissionForm />
							)}
							{!playerSowStore.isFormRequired && (
								<SowSubmissionDocumentsOnly />
							)}
						</>
					)}
					{!playerSowStore.isSubmissionRequired && (
						<SowMessageDisplay />
					)}
				</PlayerSowStoreContext.Provider>
			</div>
		</div>
	);
});
